import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import EnhancedPageTitle from "../../Component/EnhancedPageTitle";
import { updateConfiguration } from "../../services/configurationService";
import { useNavigate } from "react-router-dom";
import ConfirmationDialog from "../../Component/ConfirmationDialog";

interface FormState {
  conversionRate: string;
  incentiveRate: string;
  startDate: string;
  endDate: string;
}

const ConfigurationScreen: React.FC = () => {
  const navigate = useNavigate();

  const initialState: FormState = {
    conversionRate: "85",
    incentiveRate: "5",
    startDate: "",
    endDate: "",
  };

  const [form, setForm] = useState<FormState>(initialState);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [showDialog, setShowDialog] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "warning">("success");

  const fields = [
    { name: "conversionRate", label: "Conversion Rate" },
    { name: "incentiveRate", label: "Incentive Rate" },
    { name: "startDate", label: "Start Date", type: "date" },
    { name: "endDate", label: "End Date", type: "date" },
  ];

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const next = inputRefs.current[index + 1];
      if (next) next.focus();
    }
  };

  const handleUpdate = () => {
    const { conversionRate, incentiveRate, startDate, endDate } = form;

    if (!conversionRate.trim() || !incentiveRate.trim() || !startDate.trim() || !endDate.trim()) {
      setSnackbarMessage("Please fill all the fields.");
      setSnackbarSeverity("warning");
      setShowSnackbar(true);
      return;
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      setSnackbarMessage("Please enter valid Start and End Dates.");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }

    if (start >= end) {
      setSnackbarMessage("Start Date must be before End Date.");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }

    if (isNaN(Number(conversionRate)) || isNaN(Number(incentiveRate))) {
      setSnackbarMessage("Conversion Rate and Incentive Rate must be valid numbers.");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }

    setShowDialog(true);
  };

  const handleConfirmUpdate = async () => {
    setShowDialog(false);

    try {
      const payload = {
        incentiveValue: parseFloat(form.incentiveRate),
        conversionValue: parseFloat(form.conversionRate),
        effectiveFrom: form.startDate,
        effectiveTo: form.endDate,
      };

      await updateConfiguration(payload);

      setSnackbarMessage("Configuration updated successfully!");
      setSnackbarSeverity("success");
      setShowSnackbar(true);
    } catch (error) {
      console.error("Error updating configuration:", error);
      setSnackbarMessage("Failed to update configuration.");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
    }
  };

  const handleCancel = () => {
    setForm(initialState);
    inputRefs.current[0]?.focus();
  };

  const handleHistory = () => {
    navigate("/configuration-history");
  };

  return (
    <Box sx={{ bgcolor: "#f0f2f5", p: 3, minHeight: "100vh" }}>
      <Card
        elevation={1}
        sx={{
          bgcolor: "#fff",
          width: "calc(100% - 32px)",
          m: 2,
          p: 3,
          boxSizing: "border-box",
          minHeight: "80vh",
        }}
      >
        <EnhancedPageTitle
          title="Configuration"
          icon={<CurrencyExchangeIcon />}
          button1Text="History"
          button1Action={handleHistory}
        />

        <Grid container spacing={2} maxWidth="600px" direction="column">
          {fields.map(({ name, label, type }, index) => (
            <Grid item key={name}>
              <Box display="flex" alignItems="center" gap={2}>
                <Typography sx={{ width: "200px", textAlign: "right", pr: 1 }}>
                  {label}
                </Typography>
                <Box display="flex" alignItems="center" sx={{ flex: 1 }}>
                  <TextField
                    name={name}
                    value={form[name as keyof FormState]}
                    onChange={handleChange}
                    size="small"
                    type={type || "text"}
                    InputLabelProps={
                      type === "date" ? { shrink: true } : undefined
                    }
                    inputRef={(el) => (inputRefs.current[index] = el)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    sx={{ width: "200px" }}
                  />
                  {(name === "conversionRate" || name === "incentiveRate") && (
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, minWidth: "60px", textAlign: "left" }}
                      color="text.secondary"
                    >
                      {name === "conversionRate" ? "USD/INR" : "%"}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        <ConfirmationDialog
          open={showDialog}
          title="Confirm Update"
          content="Are you sure you want to update the configuration?"
          onConfirm={handleConfirmUpdate}
          onCancel={() => setShowDialog(false)}
        />

        <Snackbar
          open={showSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowSnackbar(false)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => setShowSnackbar(false)}
            severity={snackbarSeverity}
            variant="filled"
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <Box mt={10} display="flex" gap={1} justifyContent="flex-start">
          <Button variant="contained" color="primary" onClick={handleUpdate}>
            UPDATE
          </Button>
          <Button variant="outlined" color="error" onClick={handleCancel}>
            RESET
          </Button>
        </Box>
      </Card>
    </Box>
  );
};

export default ConfigurationScreen;
